/**
 * Player Profile Card Component
 * Displays player info with actions like message, gift, friend, and admin controls
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { getToken, clearToken } from '../../utils/tokenStorage';

const VIP_PERKS = {
  owner: { crownIcon: '👑', nameColor: '#FFD700' },
  overseer: { crownIcon: '🔱', nameColor: '#00CED1' },
  admin: { crownIcon: '⚡', nameColor: '#FF6B6B' },
  artist: { crownIcon: '🎵', nameColor: '#9B59B6' },
  vip: { crownIcon: '💎', nameColor: '#3498DB' },
  user: { crownIcon: null, nameColor: '#FFFFFF' }
};

const PlayerProfileCard = ({
  isOpen,
  onClose,
  player,
  setPlayer,
  friends,
  isAdmin,
  isOwner,
  onStartPrivateChat,
  onOpenGiftModal,
  onToggleFriend,
  user,
  socket,
  currentUserName
}) => {
  if (!isOpen || !player) return null;

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const perks = VIP_PERKS[player.role] || VIP_PERKS.user;
  const isFriend = friends.find(f => f.id === (player.userId || player.id));

  // Auth-aware fetch helper
  const pcAuthFetch = async (url, options = {}) => {
    const token = getToken();
    if (!token) { toast.error('Please log in'); return null; }
    const res = await fetch(url, {
      ...options,
      headers: { 'Authorization': `Bearer ${token}`, ...(options.headers || {}) },
    });
    if (res.status === 401) {
      clearToken();
      toast.error('Session expired — please log in again');
      return null;
    }
    return res;
  };

  const handleGiveCoins = async () => {
    try {
      const res = await pcAuthFetch(`${API_URL}/api/nightclub/vibecoins/give/${player.userId}?amount=1000`, {
        method: 'POST'
      });
      if (!res) return;
      const data = await res.json();
      if (res.ok) {
        toast.success(`Gave 1000 coins to ${player.name}!`);
      } else {
        toast.error(data.detail || 'Failed to give coins');
      }
    } catch (e) {
      toast.error('Failed to give coins');
    }
  };

  const handleKickUser = async () => {
    if (!player.userId) return;
    try {
      const res = await pcAuthFetch(`${API_URL}/api/nightclub/users/${player.userId}/kick`, {
        method: 'POST'
      });
      if (!res) return;
      const data = await res.json();
      if (res.ok) {
        toast.success(`Kicked ${player.name} from nightclub`);
        onClose();
      } else {
        toast.error(data.detail || 'Failed to kick user');
      }
    } catch (e) {
      toast.error('Failed to kick user');
    }
  };

  const handleBanUser = async () => {
    if (!player.userId) return;
    const reason = prompt('Ban reason (optional):') || 'Banned by admin';
    try {
      const res = await pcAuthFetch(`${API_URL}/api/nightclub/users/${player.userId}/ban`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason })
      });
      if (!res) return;
      const data = await res.json();
      if (res.ok) {
        toast.success(`Banned ${player.name}`);
        onClose();
      } else {
        toast.error(data.detail || 'Failed to ban user');
      }
    } catch (e) {
      toast.error('Failed to ban user');
    }
  };

  const handlePromoteUser = async (role) => {
    if (!player.userId) return;
    try {
      const res = await pcAuthFetch(`${API_URL}/api/nightclub/users/${player.userId}/promote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role })
      });
      if (!res) return;
      const data = await res.json();
      if (res.ok) {
        toast.success(`Promoted ${player.name} to ${role}`);
        setPlayer(prev => ({ ...prev, role }));
      } else {
        toast.error(data.detail || 'Failed to promote user');
      }
    } catch (e) {
      toast.error('Failed to promote user');
    }
  };

  const handleToggleVip = async () => {
    console.log('[PlayerProfileCard] Toggling VIP for userId:', player.userId, 'player:', player);
    if (!player.userId) {
      toast.error('Cannot update: User ID not available');
      return;
    }
    try {
      const newStatus = !player.isVip;
      const res = await pcAuthFetch(`${API_URL}/api/nightclub/users/${player.userId}/vip?enabled=${newStatus}`, {
        method: 'POST'
      });
      if (!res) return;
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        setPlayer(prev => ({ ...prev, isVip: newStatus }));
        // Emit notification to the target player via socket
        if (socket && newStatus) {
          socket.emit('access_granted', {
            target_user_id: player.userId,
            access_type: 'VIP',
            granted_by: currentUserName
          });
        }
      } else {
        toast.error(data.detail || 'Failed');
      }
    } catch (e) {
      console.error('[PlayerProfileCard] VIP toggle error:', e);
      toast.error('Failed to update VIP status');
    }
  };

  const handleToggleBackstage = async () => {
    console.log('[PlayerProfileCard] Toggling Backstage for userId:', player.userId);
    if (!player.userId) {
      toast.error('Cannot update: User ID not available');
      return;
    }
    try {
      const newStatus = !player.backstageAccess;
      const res = await pcAuthFetch(`${API_URL}/api/nightclub/users/${player.userId}/backstage?enabled=${newStatus}`, {
        method: 'POST'
      });
      if (!res) return;
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        setPlayer(prev => ({ ...prev, backstageAccess: newStatus }));
        // Emit notification to the target player via socket
        if (socket && newStatus) {
          socket.emit('access_granted', {
            target_user_id: player.userId,
            access_type: 'Backstage',
            granted_by: currentUserName
          });
        }
      } else {
        toast.error(data.detail || 'Failed');
      }
    } catch (e) {
      console.error('[PlayerProfileCard] Backstage toggle error:', e);
      toast.error('Failed to update backstage access');
    }
  };

  const handleToggleRooftop = async () => {
    console.log('[PlayerProfileCard] Toggling Rooftop for userId:', player.userId);
    if (!player.userId) {
      toast.error('Cannot update: User ID not available');
      return;
    }
    try {
      const newStatus = !player.rooftopAccess;
      const res = await pcAuthFetch(`${API_URL}/api/nightclub/users/${player.userId}/rooftop?enabled=${newStatus}`, {
        method: 'POST'
      });
      if (!res) return;
      if (res.ok) {
        setPlayer(prev => ({ ...prev, rooftopAccess: newStatus }));
        toast.success(`Rooftop access ${newStatus ? 'granted' : 'revoked'}`);
        // Emit notification to the target player via socket
        if (socket && newStatus) {
          socket.emit('access_granted', {
            target_user_id: player.userId,
            access_type: 'Rooftop',
            granted_by: currentUserName
          });
        }
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to update rooftop access');
      }
    } catch (e) {
      console.error('[PlayerProfileCard] Rooftop toggle error:', e);
      toast.error('Failed to update rooftop access');
    }
  };

  // Owner-only toggles
  const handleToggleAdminCommands = async () => {
    if (!player.userId) {
      toast.error('Cannot update: User ID not available');
      return;
    }
    try {
      const newStatus = !player.adminCommandsAccess;
      const res = await pcAuthFetch(`${API_URL}/api/nightclub/users/${player.userId}/admin-commands?enabled=${newStatus}`, {
        method: 'POST'
      });
      if (!res) return;
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        setPlayer(prev => ({ ...prev, adminCommandsAccess: newStatus }));
        if (socket && newStatus) {
          socket.emit('access_granted', {
            target_user_id: player.userId,
            access_type: 'Admin Commands',
            granted_by: currentUserName
          });
        }
      } else {
        toast.error(data.detail || 'Failed');
      }
    } catch (e) {
      toast.error('Failed to update admin commands access');
    }
  };

  const handleToggleLightingControls = async () => {
    if (!player.userId) {
      toast.error('Cannot update: User ID not available');
      return;
    }
    try {
      const newStatus = !player.lightingAccess;
      const res = await pcAuthFetch(`${API_URL}/api/nightclub/users/${player.userId}/lighting-controls?enabled=${newStatus}`, {
        method: 'POST'
      });
      if (!res) return;
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        setPlayer(prev => ({ ...prev, lightingAccess: newStatus }));
        if (socket && newStatus) {
          socket.emit('access_granted', {
            target_user_id: player.userId,
            access_type: 'Lighting Controls',
            granted_by: currentUserName
          });
        }
      } else {
        toast.error(data.detail || 'Failed');
      }
    } catch (e) {
      toast.error('Failed to update lighting controls access');
    }
  };

  const handleToggleVisualizerAccess = async () => {
    if (!player.userId) {
      toast.error('Cannot update: User ID not available');
      return;
    }
    try {
      const newStatus = !player.visualizerAccess;
      const res = await pcAuthFetch(`${API_URL}/api/nightclub/users/${player.userId}/visualizer-access?enabled=${newStatus}`, {
        method: 'POST'
      });
      if (!res) return;
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        setPlayer(prev => ({ ...prev, visualizerAccess: newStatus }));
        if (socket && newStatus) {
          socket.emit('access_granted', {
            target_user_id: player.userId,
            access_type: 'Room Visualizer',
            granted_by: currentUserName
          });
        }
      } else {
        toast.error(data.detail || 'Failed');
      }
    } catch (e) {
      toast.error('Failed to update visualizer access');
    }
  };

  // Toggle switch component for cleaner code
  const ToggleSwitch = ({ label, icon, enabled, onToggle, color, testId }) => (
    <div className="flex items-center justify-between">
      <span className="text-white/80 text-sm flex items-center gap-1.5">
        {icon && <span>{icon}</span>}
        {label}
      </span>
      <button
        onClick={onToggle}
        className={`relative w-12 h-6 rounded-full transition-colors ${
          enabled ? color : 'bg-gray-600'
        }`}
        data-testid={testId}
      >
        <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
          enabled ? 'left-7' : 'left-1'
        }`} />
      </button>
    </div>
  );

  return (
    <div 
      className="fixed inset-0 z-[9990] flex items-center justify-center bg-black/80 p-4"
      onClick={onClose}
      data-testid="player-profile-overlay"
    >
      <Card 
        className="w-full max-w-sm bg-gray-900 border-purple-500/30 max-h-[90vh] overflow-y-auto" 
        onClick={e => e.stopPropagation()}
        data-testid="player-profile-card"
      >
        <CardHeader className="text-center pb-2">
          <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-4xl mb-2">
            {perks.crownIcon || '👤'}
          </div>
          <CardTitle className="text-white flex items-center justify-center gap-2">
            <span style={{ color: perks.nameColor }}>
              {player.name || 'Player'}
            </span>
            {perks.crownIcon && <span>{perks.crownIcon}</span>}
          </CardTitle>
          <p className="text-white/60 text-sm capitalize">{player.role || 'User'}</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Button 
              onClick={() => onStartPrivateChat(player.id, player.name)}
              className="flex-1 bg-purple-600 hover:bg-purple-500"
              data-testid="message-player-btn"
            >
              💬 Message
            </Button>
            <Button 
              onClick={() => onOpenGiftModal(player.id, player.name)}
              className="flex-1 bg-pink-600 hover:bg-pink-500"
              data-testid="gift-player-btn"
            >
              🎁 Gift
            </Button>
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={() => onToggleFriend(player.userId || player.id, player.name)}
              variant="outline"
              className={`flex-1 ${isFriend ? 'border-red-500 text-red-400' : 'border-green-500 text-green-400'}`}
              data-testid="toggle-friend-btn"
            >
              {isFriend ? '💔 Unfriend' : '💚 Add Friend'}
            </Button>
          </div>
          
          {/* Give Coins Button - Admin Only */}
          {isAdmin && player.userId && (
            <Button
              onClick={handleGiveCoins}
              className="w-full bg-gradient-to-r from-yellow-600 to-amber-600 hover:from-yellow-500 hover:to-amber-500"
              data-testid="give-coins-btn"
            >
              Give 1000 Coins
            </Button>
          )}
          
          {/* Kick / Ban / Promote - Admin Only */}
          {isAdmin && player.userId && (
            <div className="bg-gradient-to-r from-red-900/30 to-orange-900/20 rounded-lg p-3 space-y-2 border border-red-500/30">
              <p className="text-red-400 text-xs font-semibold">MODERATION</p>
              <div className="flex gap-2">
                <Button
                  onClick={handleKickUser}
                  variant="outline"
                  className="flex-1 border-orange-500/50 text-orange-400 hover:bg-orange-500/20 text-xs"
                  data-testid="kick-user-btn"
                >
                  Kick
                </Button>
                <Button
                  onClick={handleBanUser}
                  variant="outline"
                  className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/20 text-xs"
                  data-testid="ban-user-btn"
                >
                  Ban
                </Button>
              </div>
              {isOwner && (
                <div className="space-y-1">
                  <p className="text-white/40 text-[10px]">Promote to:</p>
                  <div className="flex gap-1.5">
                    {['user', 'vip', 'artist', 'admin'].map(role => (
                      <Button
                        key={role}
                        onClick={() => handlePromoteUser(role)}
                        variant="outline"
                        className={`flex-1 text-[10px] py-1 h-7 ${
                          player.role === role 
                            ? 'border-green-500/50 text-green-400 bg-green-500/10' 
                            : 'border-white/20 text-white/60 hover:border-purple-500/50'
                        }`}
                        data-testid={`promote-${role}-btn`}
                      >
                        {role.charAt(0).toUpperCase() + role.slice(1)}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <div className="bg-black/30 rounded-lg p-3 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-white/60">Room</span>
              <span className="text-white">{player.room || 'Main Floor'}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-white/60">Status</span>
              <span className="text-green-400">Online</span>
            </div>
          </div>
          
          {/* Admin Controls - Access Permissions */}
          {isAdmin && player.userId && (
            <div className="bg-gradient-to-r from-amber-900/30 to-purple-900/30 rounded-lg p-3 space-y-3 border border-amber-500/30">
              <p className="text-amber-400 text-xs font-semibold flex items-center gap-1">
                ⚡ ACCESS CONTROLS
              </p>
              
              <ToggleSwitch
                label="VIP Status"
                icon="💎"
                enabled={player.isVip}
                onToggle={handleToggleVip}
                color="bg-amber-500"
                testId="toggle-vip"
              />
              
              <ToggleSwitch
                label="Backstage Access"
                icon="🎤"
                enabled={player.backstageAccess}
                onToggle={handleToggleBackstage}
                color="bg-purple-500"
                testId="toggle-backstage"
              />
              
              <ToggleSwitch
                label="Rooftop Access"
                icon="🏙️"
                enabled={player.rooftopAccess}
                onToggle={handleToggleRooftop}
                color="bg-sky-500"
                testId="toggle-rooftop"
              />
            </div>
          )}
          
          {/* Owner-Only Controls - Special Permissions */}
          {isOwner && player.userId && (
            <div className="bg-gradient-to-r from-yellow-900/40 to-red-900/30 rounded-lg p-3 space-y-3 border border-yellow-500/40">
              <p className="text-yellow-400 text-xs font-semibold flex items-center gap-1">
                👑 OWNER CONTROLS
              </p>
              
              <ToggleSwitch
                label="Admin Commands"
                icon="⚡"
                enabled={player.adminCommandsAccess}
                onToggle={handleToggleAdminCommands}
                color="bg-red-500"
                testId="toggle-admin-commands"
              />
              
              <ToggleSwitch
                label="Lighting Controls"
                icon="💡"
                enabled={player.lightingAccess}
                onToggle={handleToggleLightingControls}
                color="bg-cyan-500"
                testId="toggle-lighting"
              />
              
              <ToggleSwitch
                label="Room Visualizer"
                icon="🎨"
                enabled={player.visualizerAccess}
                onToggle={handleToggleVisualizerAccess}
                color="bg-pink-500"
                testId="toggle-visualizer"
              />
              
              <p className="text-white/40 text-xs mt-2">
                These permissions allow this user to control club features
              </p>
            </div>
          )}
          
          <Button 
            onClick={onClose}
            variant="ghost"
            className="w-full text-white/60"
            data-testid="close-profile-btn"
          >
            Close
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default PlayerProfileCard;
